import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { C, FONT, clamp, eyebrowSx, labelSx } from "../accountStyles";
import { ArtworkThumb, CardButton, StatusTag } from "./primitives";

/*
 * Presentation pieces for the Invoices panel. All data and handlers come from
 * invoice.jsx; these components only lay the existing fields out.
 */

export const invoiceTone = (status) => (status === "PAID" ? "success" : "danger");
const money = (value) => `₱${Number(value).toLocaleString()}`;

/** Grid / list switch (same two modes as before). */
export function ViewToggle({ viewMode, onChange }) {
  const btn = (mode, label, Icon, first) => (
    <IconButton
      size="small"
      onClick={() => onChange(mode)}
      aria-label={label}
      aria-pressed={viewMode === mode}
      sx={{
        borderRadius: 0,
        width: 40,
        height: 40,
        borderLeft: first ? "none" : `1px solid ${C.lineStrong}`,
        bgcolor: viewMode === mode ? C.ink : "transparent",
        color: viewMode === mode ? "#fff" : C.secondary,
        "&:hover": { bgcolor: viewMode === mode ? C.ink : C.soft },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );
  return (
    <Box role="group" aria-label="Display" sx={{ display: "inline-flex", border: `1px solid ${C.lineStrong}`, bgcolor: C.paper }}>
      {btn("grid", "Grid view", GridViewIcon, true)}
      {btn("list", "List view", ViewListIcon, false)}
    </Box>
  );
}

/** Column labels shown above the list view on tablet and desktop. */
export function InvoiceListHead() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: { xs: "none", md: "grid" },
        gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 2fr) 120px 150px 24px",
        gap: 2,
        px: 2.5,
        pb: 1.5,
        borderBottom: `1px solid ${C.ink}`,
      }}
    >
      {["Invoice", "Auction", "Status", "Amount", ""].map((h, i) => (
        <Typography key={i} sx={{ ...eyebrowSx, textAlign: i === 3 ? "right" : "left" }}>
          {h}
        </Typography>
      ))}
    </Box>
  );
}

/** One invoice in list (row) or grid (card) mode. */
export function InvoiceItem({ invoice, viewMode, onOpen }) {
  const auction = invoice.auction_name || "Auction";

  if (viewMode === "grid") {
    return (
      <CardButton onClick={onOpen}>
        <Box sx={{ p: { xs: 2.5, md: 3 }, display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
            <Typography sx={eyebrowSx}>Invoice #{invoice.invoice_no}</Typography>
            <StatusTag label={invoice.status} tone={invoiceTone(invoice.status)} />
          </Box>
          <Typography className="acc-title" sx={{ fontFamily: FONT, fontSize: { xs: "1.15rem", md: "1.3rem" }, color: C.ink, lineHeight: 1.3, ...clamp(2), minHeight: "2.6em" }}>
            {auction}
          </Typography>
          <Box sx={{ mt: "auto", pt: 2, borderTop: `1px solid ${C.line}`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Typography sx={{ ...eyebrowSx, fontWeight: 400 }}>Total</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: "1.2rem", fontWeight: 700, color: C.ink }}>{money(invoice.total_amount)}</Typography>
          </Box>
        </Box>
      </CardButton>
    );
  }

  return (
    <CardButton
      onClick={onOpen}
      sx={{ border: "none", borderBottom: `1px solid ${C.line}`, "&:hover, &.Mui-focusVisible": { bgcolor: C.soft } }}
    >
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "minmax(0, 1.1fr) minmax(0, 2fr) 120px 150px 24px" },
          gridTemplateAreas: { xs: '"no status" "auction auction" "amount amount"', md: '"no auction status amount chev"' },
          alignItems: "center",
          columnGap: 2,
          rowGap: 0.75,
          px: { xs: 2, md: 2.5 },
          py: { xs: 2.25, md: 2.5 },
        }}
      >
        <Typography sx={{ ...labelSx, fontSize: "0.72rem", gridArea: "no", wordBreak: "break-word" }}>#{invoice.invoice_no}</Typography>
        <Typography className="acc-title" sx={{ fontFamily: FONT, fontSize: "1.05rem", color: C.ink, lineHeight: 1.35, gridArea: "auction", ...clamp(2) }}>
          {auction}
        </Typography>
        <Box sx={{ gridArea: "status", justifySelf: { xs: "end", md: "start" }, minWidth: 0, maxWidth: "100%" }}>
          <StatusTag label={invoice.status} tone={invoiceTone(invoice.status)} sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }} />
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: "1.1rem", fontWeight: 700, color: C.ink, gridArea: "amount", textAlign: { xs: "left", md: "right" }, whiteSpace: "nowrap" }}>
          {money(invoice.total_amount)}
        </Typography>
        <ChevronRightIcon aria-hidden="true" sx={{ gridArea: "chev", display: { xs: "none", md: "block" }, color: C.secondary }} />
      </Box>
    </CardButton>
  );
}

/** Invoice detail — summary band, purchased items and total. */
export function InvoiceSummary({ invoiceDetail, selectedInvoice }) {
  return (
    <Box
      sx={{
        mb: { xs: 4, md: 5 },
        p: { xs: 2.5, sm: 4 },
        bgcolor: C.soft,
        border: `1px solid ${C.line}`,
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr) minmax(0, 1fr)", md: "minmax(0, 1.4fr) minmax(0, 1fr) auto" },
        gap: { xs: 2.5, md: 5 },
        alignItems: "start",
      }}
    >
      <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, minWidth: 0 }}>
        <Typography sx={eyebrowSx}>Invoice No.</Typography>
        <Typography component="h2" sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: "1.5rem", md: "1.9rem" }, color: C.ink, mt: 0.5, lineHeight: 1.2, wordBreak: "break-word" }}>
          {invoiceDetail.data.invoice_no}
        </Typography>
        {selectedInvoice.auction_name && (
          <Typography sx={{ fontFamily: FONT, color: C.secondary, mt: 0.75, fontSize: "0.95rem" }}>{selectedInvoice.auction_name}</Typography>
        )}
      </Box>
      <Box>
        <Typography sx={eyebrowSx}>Status</Typography>
        <Box sx={{ mt: 1 }}>
          <StatusTag label={selectedInvoice.status} tone={invoiceTone(selectedInvoice.status)} />
        </Box>
      </Box>
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        <Typography sx={eyebrowSx}>Total Amount</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: { xs: "1.2rem", sm: "1.6rem" }, fontWeight: 700, color: C.ink, mt: 0.5, wordBreak: "break-word" }}>
          {money(invoiceDetail.data.total_amount)}
        </Typography>
      </Box>
    </Box>
  );
}

export function InvoiceItems({ lots, viewMode, total }) {
  const isGrid = viewMode === "grid";
  return (
    <Box>
      <Box
        sx={
          isGrid
            ? { display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: { xs: 1.5, md: 3 } }
            : { borderTop: `1px solid ${C.ink}` }
        }
      >
        {lots?.map((lot) => (
          <Box
            key={lot.id}
            sx={{
              display: "flex",
              flexDirection: isGrid ? "column" : "row",
              gap: isGrid ? 0 : { xs: 2, sm: 3 },
              py: isGrid ? 0 : { xs: 2, sm: 2.5 },
              border: isGrid ? `1px solid ${C.line}` : "none",
              borderBottom: `1px solid ${C.line}`,
              minWidth: 0,
            }}
          >
            <ArtworkThumb
              src={lot.img_url}
              alt={lot.title || ""}
              ratio={isGrid ? "4 / 5" : "1 / 1"}
              sx={isGrid ? undefined : { width: { xs: 84, sm: 120 }, flexShrink: 0 }}
            />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: isGrid ? "column" : { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: isGrid ? "stretch" : { xs: "stretch", sm: "center" },
                gap: 1,
                p: isGrid ? { xs: 1.5, md: 2 } : 0,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={eyebrowSx}>Lot {lot.lot_number}</Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.5px", textTransform: "uppercase", color: C.ink, mt: 0.5, ...clamp(2) }}>
                  {lot.artist}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontStyle: "italic", fontSize: "0.95rem", color: "#444", lineHeight: 1.35, mt: 0.25, ...clamp(3) }}>{lot.title}</Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: C.ink,
                  whiteSpace: "nowrap",
                  mt: isGrid ? "auto" : 0,
                  pt: isGrid ? 1.5 : 0,
                  borderTop: isGrid ? `1px solid ${C.line}` : "none",
                }}
              >
                {money(lot.total_price)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Box sx={{ width: { xs: "100%", sm: 320 }, borderTop: `2px solid ${C.ink}`, pt: 2, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Typography sx={labelSx}>Total</Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "1.35rem", color: C.ink }}>{money(total)}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
